import { useState } from 'react';
import { Play, Film, ExternalLink, Instagram } from 'lucide-react';
import { parseCrewVideo, youtubeThumb, type ParsedCrewVideo } from '@/lib/videoEmbed';
import { cn } from '@/lib/utils';

/**
 * "The Part" — a crew's headline video / montage.
 *
 * Skate crews are defined by their footage: the full part on YouTube, the clip
 * on Instagram. This renders that footage as the loud, cinematic centrepiece it
 * deserves — not a link buried in a link list.
 *
 * - **YouTube**: click-to-load facade (thumbnail + big play button) so we don't
 *   ship a heavy iframe / tracking cookies until the viewer actually hits play.
 * - **Instagram**: embedded permalink.
 *
 * Returns null if the URL isn't a recognised YouTube/Instagram link.
 */
export function CrewVideoSection({
  videoUrl,
  crewName,
  accentHex = '#14b8a6',
}: {
  videoUrl: string | undefined;
  crewName: string;
  accentHex?: string;
}) {
  const parsed = parseCrewVideo(videoUrl);
  if (!parsed) return null;

  return (
    <section
      className="relative overflow-hidden rounded-3xl border bg-black"
      style={{ borderColor: `${accentHex}33` }}
    >
      {/* Header ribbon */}
      <div className="flex items-center justify-between gap-3 px-4 pt-4 sm:px-5">
        <div className="flex items-center gap-2">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-xl"
            style={{ background: `${accentHex}1f`, color: accentHex }}
          >
            <Film className="h-4.5 w-4.5" />
          </div>
          <div>
            <h2 className="text-lg font-black uppercase leading-none text-white" style={{ fontFamily: 'Impact, "Arial Black", sans-serif' }}>
              The Part
            </h2>
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: accentHex }}>
              {parsed.source === 'youtube' ? 'Full video · montage' : 'Latest clip'}
            </p>
          </div>
        </div>
        <a
          href={parsed.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs font-bold text-gray-300 transition-colors hover:border-white/20 hover:text-white"
        >
          {parsed.source === 'instagram' ? <Instagram className="h-3.5 w-3.5" /> : <ExternalLink className="h-3.5 w-3.5" />}
          Watch
        </a>
      </div>

      <div className="p-4 sm:p-5">
        {parsed.source === 'youtube' ? (
          <YouTubeFacade video={parsed} crewName={crewName} accentHex={accentHex} />
        ) : (
          <InstagramEmbed video={parsed} />
        )}
      </div>
    </section>
  );
}

/** Click-to-load YouTube: poster + play button, swaps to iframe on demand. */
function YouTubeFacade({
  video,
  crewName,
  accentHex,
}: {
  video: ParsedCrewVideo;
  crewName: string;
  accentHex: string;
}) {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-black">
        <iframe
          src={`${video.embedUrl}&autoplay=1`}
          title={`${crewName} — video`}
          className="absolute inset-0 h-full w-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className="group relative block aspect-video w-full overflow-hidden rounded-2xl bg-black"
      aria-label={`Play ${crewName} video`}
    >
      {video.id && (
        <img
          src={youtubeThumb(video.id)}
          alt={`${crewName} video thumbnail`}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      )}
      {/* Cinematic vignette */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20" />
      {/* Play button */}
      <span
        className={cn(
          'absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full shadow-2xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95',
        )}
        style={{ background: accentHex, color: '#000', boxShadow: `0 0 40px ${accentHex}88` }}
      >
        <Play className="h-7 w-7 translate-x-0.5 fill-current" />
      </span>
    </button>
  );
}

/** Instagram permalink embed. */
function InstagramEmbed({ video }: { video: ParsedCrewVideo }) {
  return (
    <div className="mx-auto w-full max-w-md overflow-hidden rounded-2xl border border-white/10 bg-black">
      <iframe
        src={video.embedUrl}
        title="Instagram clip"
        className="h-[560px] w-full"
        allow="encrypted-media; picture-in-picture"
        referrerPolicy="strict-origin-when-cross-origin"
        loading="lazy"
        scrolling="no"
      />
    </div>
  );
}
